import dayjs from 'dayjs'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined'

import HeartbeatLoader from '@/components/ui/loaders/HeartbeatLoader'
import { labelToTranslationKey } from '@/pages/diagnostic/components/Mesures'
import { useGetWaitingRoomConsultationPreviewQuery } from '@/core/services/waiting-room'
import { WaitingRoomConsultationPreview } from '@/core/models/waiting-room.model'

// The mongoose placeholder stored when no identification was captured (see PatientDetails).
const SECU_PLACEHOLDER = '000000000000000'

type ConsultationPreviewDrawerProps = {
  waitingRoomId: string
  // null = closed; the drawer fetches the preview fresh each time it opens on a consultation
  consultationId: string | null
  onClose: () => void
}

function fullName(person: { name: string; surname: string } | null): string {
  return person ? `${person.name} ${person.surname}`.trim() : ''
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="primary" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {children}
    </Stack>
  )
}

function LabeledValue({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Stack direction="row" spacing={1} justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  )
}

function PreviewContent({ preview }: { preview: WaitingRoomConsultationPreview }) {
  const { t } = useTranslation()

  const genderLabel = (gender: string | null) =>
    gender === 'M'
      ? t('field.gender.options.male.label')
      : gender === 'F'
      ? t('field.gender.options.female.label')
      : gender ?? '—'
  const identification = !preview.patient?.secu || preview.patient.secu === SECU_PLACEHOLDER ? t('page.waitingRoom.preview.patient.unknownId') : preview.patient.secu
  // Assumption (to confirm with SEHA): "the physician implicated" = the assigned medecin when
  // one exists; the nurse (pds, else createdBy) is shown separately as the preparer.
  const preparedBy = fullName(preview.pds) || fullName(preview.createdBy)
  const recordedMesures = preview.mesures.filter((mesure) => mesure.value !== null && mesure.value !== '')

  return (
    <>
      <Section title={t('page.waitingRoom.preview.patient.title')}>
        <Typography variant="body1" sx={{ fontWeight: 600 }} data-testid="preview-patient-name">
          {preview.patient ? `${preview.patient.name} ${preview.patient.surname}`.trim() : '—'}
        </Typography>
        {preview.patient && (
          <>
            <LabeledValue
              label={t('page.waitingRoom.preview.patient.birthday')}
              value={preview.patient.birthday ? dayjs(preview.patient.birthday).format('L') : '—'}
            />
            <LabeledValue label={t('field.gender.label')} value={genderLabel(preview.patient.gender)} />
            <LabeledValue label={t('page.waitingRoom.preview.patient.id')} value={identification} />
            {preview.patient.nationality && (
              <LabeledValue label={t('page.waitingRoom.preview.patient.nationality')} value={preview.patient.nationality} />
            )}
          </>
        )}
      </Section>
      <Divider />
      <Section title={t('page.waitingRoom.preview.team.title')}>
        <LabeledValue label={t('page.waitingRoom.preview.team.preparedBy')} value={preparedBy || '—'} />
        <LabeledValue
          label={t('page.waitingRoom.preview.team.physician')}
          value={fullName(preview.medecin) || t('page.waitingRoom.preview.team.physicianUnassigned')}
        />
        {preview.speciality && <LabeledValue label={t('page.waitingRoom.preview.team.speciality')} value={preview.speciality} />}
      </Section>
      {preview.readiness && (
        <>
          <Divider />
          <Section title={t('page.waitingRoom.preview.readiness.title')}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Chip
                size="small"
                color={preview.readiness.status === 'submitted' ? 'success' : 'warning'}
                label={
                  preview.readiness.status === 'submitted'
                    ? t('page.waitingRoom.preview.readiness.ready')
                    : t('page.waitingRoom.preview.readiness.preparing')
                }
                data-testid="preview-readiness-status"
              />
              <Typography variant="body2">{preview.readiness.templateTitle}</Typography>
            </Stack>
            <LabeledValue
              label={t('page.waitingRoom.preview.readiness.progress')}
              value={
                preview.readiness.requiredTotal > 0
                  ? `${preview.readiness.answered}/${preview.readiness.requiredTotal}`
                  : `${preview.readiness.completed}/${preview.readiness.total}`
              }
            />
          </Section>
        </>
      )}
      <Divider />
      <Section title={t('page.waitingRoom.preview.chiefComplaint.title')}>
        <Typography variant="body2" data-testid="preview-chief-complaint">
          {preview.reason || t('page.waitingRoom.preview.chiefComplaint.empty')}
        </Typography>
      </Section>
      <Section title={t('page.waitingRoom.preview.comment.title')}>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {preview.commentaire || t('page.waitingRoom.preview.comment.empty')}
        </Typography>
      </Section>
      <Divider />
      <Section title={t('page.waitingRoom.preview.vitals.title')}>
        {recordedMesures.length ? (
          <Table size="small">
            <TableBody>
              {recordedMesures.map((mesure) => (
                <TableRow key={mesure.label}>
                  {/* Raw French label from the measure record — translate it. */}
                  <TableCell sx={{ pl: 0 }}>{t(labelToTranslationKey(mesure.label))}</TableCell>
                  <TableCell align="right" sx={{ pr: 0 }}>
                    {`${mesure.value} ${mesure.unit}`.trim()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('page.waitingRoom.preview.vitals.empty')}
          </Typography>
        )}
      </Section>
      <Divider />
      <Section title={t('page.waitingRoom.preview.documents.title', { count: preview.documents.length })}>
        {preview.documents.length ? (
          <Stack spacing={1}>
            {preview.documents.map((document, index) => (
              <Stack key={`${document.filename}-${index}`} direction="row" alignItems="center" spacing={1}>
                <InsertDriveFileOutlinedIcon fontSize="small" color="action" />
                {document.url ? (
                  <Link href={document.url} target="_blank" rel="noopener noreferrer" variant="body2">
                    {document.filename}
                  </Link>
                ) : (
                  <Typography variant="body2">{document.filename}</Typography>
                )}
                {document.isRecording && <Chip size="small" variant="outlined" label={t('page.waitingRoom.preview.documents.recording')} />}
              </Stack>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('page.waitingRoom.preview.documents.empty')}
          </Typography>
        )}
      </Section>
    </>
  )
}

/**
 * SEHA (en-SA) read-only case preview: what a doctor may see BEFORE accepting a waiting
 * consultation or joining an in-progress one — patient identity, preparing nurse and implicated
 * physician, chief complaint + nurse comment, vital-sign measures and attached documents.
 * Purely additive: it triggers no state change and leaves the accept/claim flow untouched.
 */
export function ConsultationPreviewDrawer({ waitingRoomId, consultationId, onClose }: ConsultationPreviewDrawerProps) {
  const { t } = useTranslation()
  const { data: preview, isFetching, isError } = useGetWaitingRoomConsultationPreviewQuery(
    consultationId ? { waitingRoomId, consultationId } : skipToken,
    { refetchOnMountOrArgChange: true }
  )

  return (
    <Drawer anchor="right" open={!!consultationId} onClose={onClose} data-testid="consultation-preview-drawer">
      <Stack spacing={2} sx={{ width: { xs: '100vw', sm: 420 }, p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{t('page.waitingRoom.preview.title')}</Typography>
          <IconButton onClick={onClose} aria-label={t('common:button.close.label')} data-testid="consultation-preview-close">
            <CloseRoundedIcon />
          </IconButton>
        </Stack>
        {isFetching ? (
          <Stack alignItems="center" sx={{ py: 6 }}>
            <HeartbeatLoader />
          </Stack>
        ) : isError ? (
          <Alert severity="error">{t('page.waitingRoom.preview.error')}</Alert>
        ) : (
          preview && <PreviewContent preview={preview} />
        )}
      </Stack>
    </Drawer>
  )
}
